import { Link } from 'react-router-dom';
import { PricingSection } from '../components/PricingSection';
import { usePaperTheme } from '@/hooks/usePaperTheme';

export function LandingPage() {
  usePaperTheme();

  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      {/* Nav */}
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Wordmark />
        <div className="flex items-center gap-6 text-petit">
          <a href="#how" className="text-charcoal-muted hover:text-charcoal transition-colors">
            How it works
          </a>
          <a href="#pricing" className="text-charcoal-muted hover:text-charcoal transition-colors">
            Pricing
          </a>
          <Link
            to="/login"
            className="text-charcoal-muted hover:text-charcoal transition-colors"
          >
            Sign in
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 pt-20 pb-24">
        <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">
          Architecture workbench
        </p>
        <h1 className="mt-5 text-5xl md:text-6xl font-semibold tracking-display leading-[1.05] text-charcoal">
          Think in diagrams.
          <br />
          Ship in infrastructure.
        </h1>
        <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-charcoal-muted">
          Clyra turns a one-line description into a real system diagram — and a
          starter Terraform or CloudFormation scaffold you can take to production.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link
            to="/signup"
            className="rounded-lg bg-charcoal px-5 py-2.5 text-sm font-medium text-paper hover:bg-charcoal-soft transition-colors"
          >
            Start free — 10 diagrams per account
          </Link>
          <a
            href="#how"
            className="rounded-lg border border-stone-default px-5 py-2.5 text-sm font-medium text-charcoal-soft hover:border-stone-strong transition-colors"
          >
            How it works
          </a>
        </div>
        <p className="mt-3 text-tiny text-charcoal-faint">
          No credit card. AWS-native today · GCP and Azure on the roadmap.
        </p>

        <HeroCanvas />
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-stone-subtle bg-paper-soft/60">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="max-w-2xl">
            <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">
              How it works
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight-ish text-charcoal">
              From idea to infrastructure in three steps.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <Step
              number="01"
              title="Describe"
              body="Say what you're building in plain English. Three-tier web app on AWS. Event pipeline. Static site with auth. Clyra interprets intent, not buzzwords."
            />
            <Step
              number="02"
              title="Iterate"
              body="Review the generated diagram on a real canvas. Add nodes, re-wire edges, rename components. Follow-up prompts refine what's already there."
            />
            <Step
              number="03"
              title="Ship code"
              body="Export starter Terraform or CloudFormation from the same diagram. A scaffold a senior engineer can finish — not a one-click deploy pretending to be one."
            />
          </div>
        </div>
      </section>

      {/* For engineers */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <div className="grid gap-16 md:grid-cols-2 md:items-start">
          <div>
            <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">
              Built for engineers
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight-ish text-charcoal">
              A workbench — not a toy.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-charcoal-muted">
              Clyra mirrors how engineers actually design systems: reason about the
              shape, map it visually, then commit the infrastructure. Your diagram is
              plain JSON. Your IaC is code you own. Nothing proprietary in between.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FactCard title="AWS-native" body="Structured Terraform and CloudFormation output, golden-tested." />
            <FactCard title="IaC-ready" body="Starter scaffolds pass terraform validate and cfn-lint. One free export; unlimited on Pro." />
            <FactCard title="BYO models" body="OpenAI, Anthropic, Gemini — your keys, your rate limits." />
            <FactCard title="JSON-native" body="Your diagram is data. Version it. Diff it. Commit it." />
            <FactCard title="Long-form prompts" body="Free plans get 2,000 characters per prompt — plenty for everyday architectures. Pro and BYO unlock 8,000 for richly specified, multi-zone systems." />
          </div>
        </div>
      </section>

      <PricingSection />

      {/* Quiet CTA */}
      <section className="border-t border-stone-subtle">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight-ish text-charcoal">
            A tool for thinking clearly.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-charcoal-muted">
            Ten diagrams per account, free. Upgrade to Pro for 200 — or bring your own AI
            keys and get 1,000 while paying only the card fee.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link
              to="/signup"
              className="rounded-lg bg-charcoal px-5 py-2.5 text-sm font-medium text-paper hover:bg-charcoal-soft transition-colors"
            >
              Create your account
            </Link>
            <Link
              to="/login"
              className="rounded-lg border border-stone-default px-5 py-2.5 text-sm font-medium text-charcoal-soft hover:border-stone-strong transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-subtle">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Wordmark small />
            <span className="text-tiny text-charcoal-faint">
              © {new Date().getFullYear()} — architecture → infrastructure.
            </span>
          </div>
          <div className="flex items-center gap-5 text-tiny text-charcoal-muted">
            <Link to="/terms" className="hover:text-charcoal transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-charcoal transition-colors">Privacy</Link>
            <Link to="/support" className="hover:text-charcoal transition-colors">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Wordmark — simple lowercase mark with a subtle custom cut on the 'y'.
// Keep it boring; the product should be the loud thing.
function Wordmark({ small = false }: { small?: boolean }) {
  const size = small ? 'text-base' : 'text-lg';
  return (
    <Link to="/" className={`${size} font-semibold tracking-tight-ish text-charcoal`}>
      clyra<span className="text-teal-500">.</span>
    </Link>
  );
}

// HeroCanvas — one calm product preview. No drop shadows, no glow, no
// glass. A framed SVG of a realistic generated diagram, paper shadow.
function HeroCanvas() {
  return (
    <div className="mt-16">
      <div className="rounded-2xl bg-paper-raised shadow-paper-lg overflow-hidden border border-stone-subtle">
        {/* chrome */}
        <div className="flex items-center justify-between border-b border-stone-subtle px-4 py-2.5">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-stone-default" />
              <span className="h-2 w-2 rounded-full bg-stone-default" />
              <span className="h-2 w-2 rounded-full bg-stone-default" />
            </span>
            <span className="text-tiny text-charcoal-faint">clyra — editor · example</span>
          </div>
          <span className="text-tiny text-charcoal-faint">solution · aws</span>
        </div>
        {/* prompt row */}
        <div className="flex items-center gap-2 border-b border-stone-subtle bg-paper-soft px-4 py-3">
          <span className="text-tiny text-teal-600">▸</span>
          <span className="truncate text-tiny text-charcoal-muted">
            A three-tier web app on AWS — ALB, ECS Fargate, RDS Postgres, Redis, S3.
          </span>
        </div>
        {/* canvas */}
        <svg viewBox="0 0 720 320" className="block h-auto w-full bg-paper-raised" aria-label="Example generated diagram">
          <defs>
            <marker id="arrow-paper" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 z" fill="#8d93a0" />
            </marker>
          </defs>
          <g fill="none" stroke="#c7c2b5" strokeWidth="1.25" markerEnd="url(#arrow-paper)">
            <path d="M150 80 L240 80" />
            <path d="M360 80 L450 80" />
            <path d="M570 80 L570 200" />
            <path d="M510 220 L420 220" />
            <path d="M300 220 L210 220" />
          </g>
          {[
            { x: 30, y: 50, label: 'Route 53', sub: 'DNS' },
            { x: 180, y: 50, label: 'ALB', sub: 'Load balancer' },
            { x: 390, y: 50, label: 'ECS Fargate', sub: '3 × app' },
            { x: 510, y: 50, label: 'RDS', sub: 'Postgres 16' },
            { x: 510, y: 190, label: 'ElastiCache', sub: 'Redis' },
            { x: 270, y: 190, label: 'S3', sub: 'Static assets' },
            { x: 90, y: 190, label: 'CloudFront', sub: 'CDN' },
          ].map((n) => (
            <g key={n.label}>
              <rect x={n.x} y={n.y} width="130" height="58" rx="10" fill="#ffffff" stroke="#dedad0" />
              <text x={n.x + 14} y={n.y + 24} style={{ font: '600 13px Inter, system-ui' }} fill="#121316">
                {n.label}
              </text>
              <text x={n.x + 14} y={n.y + 42} style={{ font: '11px Inter, system-ui' }} fill="#8d93a0">
                {n.sub}
              </text>
            </g>
          ))}
        </svg>
        {/* footer */}
        <div className="flex items-center justify-between border-t border-stone-subtle bg-paper-soft px-4 py-2 text-tiny text-charcoal-faint">
          <span>7 nodes · 5 edges · solution mode</span>
          <div className="flex items-center gap-2">
            <span className="rounded-md border border-stone-default px-2 py-0.5 text-charcoal-muted">
              Export Terraform
            </span>
            <span className="rounded-md border border-stone-default px-2 py-0.5 text-charcoal-muted">
              Save
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, body }: { number: string; title: string; body: string }) {
  return (
    <div className="rounded-xl bg-paper-raised border border-stone-subtle p-6">
      <span className="text-petit font-mono text-teal-600">{number}</span>
      <h3 className="mt-4 text-lg font-semibold tracking-tight-ish text-charcoal">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{body}</p>
    </div>
  );
}

function FactCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-stone-subtle bg-paper-raised p-4">
      <p className="text-sm font-semibold text-charcoal">{title}</p>
      <p className="mt-1 text-petit leading-relaxed text-charcoal-muted">{body}</p>
    </div>
  );
}
